import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { ProductKind } from '@iheartjane/types';

export const formatPotency = (potency?: number | string) => {
  if (typeof potency === 'string') {
    return potency;
  }
  return potency?.toFixed(2) ?? null;
};

interface PrimaryPotenciesArgs {
  primaryPercentCBD: number;
  primaryPercentTHC: number;
  product: MaterializedMenuProduct;
  productPercentTAC: number;
  selectedWeight?: string;
}

const primaryPotencies = ({
  primaryPercentCBD,
  primaryPercentTHC,
  product,
  productPercentTAC,
  selectedWeight,
}: PrimaryPotenciesArgs) => {
  const fallbackPercentTAC = productPercentTAC;

  const inventoryPotencies =
    'inventory_potencies' in product && selectedWeight
      ? product.inventory_potencies
      : [];

  const inventoryPotency = (inventoryPotencies || []).find(
    (potency) => potency['price_id'] === selectedWeight
  );

  const percentTAC = inventoryPotency?.['tac_potency'] || fallbackPercentTAC;
  const percentTHC = inventoryPotency?.['thc_potency'] || primaryPercentTHC;
  const percentCBD = inventoryPotency?.['cbd_potency'] || primaryPercentCBD;

  return { percentCBD, percentTAC, percentTHC };
};

const getPackTotal = (product: MaterializedMenuProduct) => {
  const shouldAppendPackTotal =
    ['pre-roll', 'vape'].includes(product.kind) &&
    product.root_subtype?.toLowerCase()?.includes('packs');
  if (!shouldAppendPackTotal) return;
  if (product.quantity_units && (product.quantity_value ?? 0) > 0) {
    const standardizedUnits = product.quantity_units.replace('_', ' ');
    return `/ ${product.quantity_value}${standardizedUnits} per pack`;
  }
  if (product.net_weight_grams && product.net_weight_grams > 0)
    return `/ ${product.net_weight_grams}g per pack`;

  return '';
};
interface BaseLabResultsArgs {
  percentCBDA: number;
  percentTHCA: number;
  primaryPercentCBD: number;
  primaryPercentTHC: number;
  productPercentTAC: number;
}

export const getBaseLabResults = ({
  percentCBDA,
  percentTHCA,
  primaryPercentCBD,
  primaryPercentTHC,
  productPercentTAC,
}: BaseLabResultsArgs) => {
  if (
    primaryPercentTHC ||
    primaryPercentCBD ||
    percentTHCA ||
    percentCBDA ||
    productPercentTAC
  ) {
    return [
      productPercentTAC && `TAC ${formatPotency(productPercentTAC)}%`,
      primaryPercentTHC && `THC ${formatPotency(primaryPercentTHC)}%`,
      primaryPercentCBD && `CBD ${formatPotency(primaryPercentCBD)}%`,
      percentTHCA && `THCa ${formatPotency(percentTHCA)}%`,
      percentCBDA && `CBDa ${formatPotency(percentCBDA)}%`,
    ]
      .filter(Boolean)
      .join(' ');
  }
  return null;
};

interface LabResultsArgs extends PrimaryPotenciesArgs {
  percentCBDA: number;
  percentTHCA: number;
}

export const getLabResults = ({
  percentCBDA,
  percentTHCA,
  primaryPercentCBD,
  primaryPercentTHC,
  product,
  productPercentTAC,
  selectedWeight,
}: LabResultsArgs) => {
  const { percentCBD, percentTAC, percentTHC } = primaryPotencies({
    primaryPercentCBD,
    primaryPercentTHC,
    product,
    productPercentTAC,
    selectedWeight,
  });
  if (percentTHC || percentCBD || percentTHCA || percentCBDA || percentTAC) {
    return [
      percentTAC && `TAC ${formatPotency(percentTAC)}%`,
      percentTHC && `THC ${formatPotency(percentTHC)}%`,
      percentCBD && `CBD ${formatPotency(percentCBD)}%`,
      percentTHCA && `THCa ${formatPotency(percentTHCA)}%`,
      percentCBDA && `CBDa ${formatPotency(percentCBDA)}%`,
    ]
      .filter(Boolean)
      .join(' ');
  }
  return null;
};

export const getDosageAmountPackInfo = (product: MaterializedMenuProduct) => {
  if (isNonCannabisKind(product.kind)) {
    return null;
  }

  const { amount, dosage } = product;

  if (dosage || amount) {
    const packTotal = getPackTotal(product);

    return [dosage, amount, packTotal].filter(Boolean).join(' ');
  }
  return '';
};

type NonCannabisKind = Extract<ProductKind, 'gear' | 'merch'>;

const isNonCannabisKind = (
  productKind: ProductKind
): productKind is NonCannabisKind =>
  productKind === 'merch' || productKind === 'gear';
